import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, setCsrf } from "./api";
import { FormEvent, useState } from "react";

const NAV = [
  ["/", "داشبورد"],
  ["/licenses", "لایسنس‌ها"],
  ["/customers", "مشتریان"],
  ["/products", "محصولات"],
  ["/plans", "پلن‌ها"],
  ["/activations", "فعال‌سازی‌ها"],
  ["/domains", "دامنه‌ها"],
  ["/releases", "انتشارها"],
  ["/events", "رویدادها"],
  ["/audit", "حسابرسی"],
  ["/admins", "مدیران"],
  ["/security", "امنیت"],
  ["/settings", "تنظیمات"],
] as const;

type Me = { id: string; email: string; role: string; csrf: string };

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<Shell />} />
    </Routes>
  );
}

function Shell() {
  const me = useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      const data = await api<Me>("/admin/auth/me");
      setCsrf(data.csrf);
      return data;
    },
    retry: false,
  });
  if (me.isError) return <Navigate to="/login" replace />;
  if (me.isLoading) return <div className="p-10 text-zinc-400">در حال بارگذاری…</div>;
  return (
    <div className="min-h-screen grid grid-cols-[240px_1fr]">
      <aside className="border-l border-line bg-panel p-4">
        <div className="mb-8">
          <div className="text-mint font-semibold tracking-wide">WEBRAAN</div>
          <div className="text-xs text-zinc-500">Licensing Platform</div>
        </div>
        <nav className="space-y-1">
          {NAV.map(([to, label]) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              className={({ isActive }) =>
                `block rounded-md px-3 py-2 text-sm ${isActive ? "bg-mint text-ink" : "text-zinc-300 hover:bg-zinc-800"}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-8 text-xs text-zinc-500 ltr">{me.data?.email}</div>
      </aside>
      <main className="p-8">
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/licenses" element={<LicensesPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/activations" element={<TablePage path="/admin/activations" title="فعال‌سازی‌ها" />} />
          <Route path="/domains" element={<TablePage path="/admin/domains" title="دامنه‌ها" />} />
          <Route path="/releases" element={<ReleasesPage />} />
          <Route path="/events" element={<TablePage path="/admin/events" title="رویدادها" />} />
          <Route path="/audit" element={<TablePage path="/admin/audit-logs" title="حسابرسی" />} />
          <Route path="/admins" element={<AdminsPage />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </main>
    </div>
  );
}

function LoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [phase, setPhase] = useState<"login" | "totp">("login");
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (phase === "login") {
        const res = await api<{ totp_required?: boolean; totp_setup?: { otpauthUrl: string }; csrf?: string; ok?: boolean }>(
          "/admin/auth/login",
          { method: "POST", body: JSON.stringify({ email, password }) },
        );
        if (res.totp_required) {
          setPhase("totp");
          setSetupUrl(res.totp_setup?.otpauthUrl ?? null);
          return;
        }
        if (res.csrf) setCsrf(res.csrf);
        nav("/");
      } else {
        const res = await api<{ ok: boolean; csrf: string }>("/admin/auth/totp", {
          method: "POST",
          body: JSON.stringify({ code: otp }),
        });
        setCsrf(res.csrf);
        nav("/");
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center">
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl border border-line bg-panel p-8 space-y-4">
        <h1 className="text-xl font-semibold">ورود به پنل لایسنس</h1>
        {phase === "login" ? (
          <>
            <input className="input" placeholder="ایمیل" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input className="input" type="password" placeholder="رمز" value={password} onChange={(e) => setPassword(e.target.value)} />
          </>
        ) : (
          <>
            {setupUrl && <p className="text-xs text-zinc-400 break-all ltr">TOTP setup: {setupUrl}</p>}
            <input className="input ltr" placeholder="کد ۶ رقمی" value={otp} onChange={(e) => setOtp(e.target.value)} />
          </>
        )}
        {error && <p className="text-red-400 text-sm">{error}</p>}
        <button className="btn">{phase === "login" ? "ادامه" : "تأیید TOTP"}</button>
      </form>
      <style>{`
        .input { width:100%; background:#111; border:1px solid #2a2a2a; border-radius:8px; padding:10px 12px; color:white; }
        .btn { width:100%; background:#0EFDC2; color:#0F0F0F; border-radius:8px; padding:10px; font-weight:600; }
      `}</style>
    </div>
  );
}

function Card({ title, value }: { title: string; value: number | string }) {
  return (
    <div className="rounded-xl border border-line bg-panel p-5">
      <div className="text-zinc-500 text-sm">{title}</div>
      <div className="text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}

function Dashboard() {
  const q = useQuery({ queryKey: ["dash"], queryFn: () => api<Record<string, number>>("/admin/dashboard") });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">داشبورد</h1>
      <div className="grid grid-cols-4 gap-4">
        <Card title="لایسنس" value={q.data?.licenses ?? "—"} />
        <Card title="مشتری" value={q.data?.customers ?? "—"} />
        <Card title="فعال‌سازی فعال" value={q.data?.activations ?? "—"} />
        <Card title="ابطال‌شده" value={q.data?.revoked ?? "—"} />
      </div>
    </div>
  );
}

function JsonTable({ rows }: { rows: unknown }) {
  return (
    <pre className="mt-4 overflow-auto rounded-xl border border-line bg-panel p-4 text-xs ltr text-zinc-300">
      {JSON.stringify(rows, null, 2)}
    </pre>
  );
}

function TablePage({ path, title }: { path: string; title: string }) {
  const q = useQuery({ queryKey: [path], queryFn: () => api(path) });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">{title}</h1>
      {q.isLoading ? "…" : <JsonTable rows={q.data} />}
    </div>
  );
}

function CustomersPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customers"], queryFn: () => api("/admin/customers") });
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const mut = useMutation({
    mutationFn: () => api("/admin/customers", { method: "POST", body: JSON.stringify({ email, name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["customers"] }),
  });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">مشتریان</h1>
      <form
        className="flex gap-2 mb-4"
        onSubmit={(e) => {
          e.preventDefault();
          mut.mutate();
        }}
      >
        <input className="bg-panel border border-line rounded px-3 py-2" placeholder="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <input className="bg-panel border border-line rounded px-3 py-2" placeholder="name" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="bg-mint text-ink rounded px-4">ایجاد</button>
      </form>
      <JsonTable rows={q.data} />
    </div>
  );
}

function ProductsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["products"], queryFn: () => api("/admin/products") });
  const [slug, setSlug] = useState("webraan-core");
  const [name, setName] = useState("Webraan Core");
  const mut = useMutation({
    mutationFn: () => api("/admin/products", { method: "POST", body: JSON.stringify({ slug, name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["products"] }),
  });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">محصولات</h1>
      <form className="flex gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <input className="bg-panel border border-line rounded px-3 py-2 ltr" value={slug} onChange={(e) => setSlug(e.target.value)} />
        <input className="bg-panel border border-line rounded px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} />
        <button className="bg-mint text-ink rounded px-4">ایجاد</button>
      </form>
      <JsonTable rows={q.data} />
    </div>
  );
}

function PlansPage() {
  const q = useQuery({ queryKey: ["plans"], queryFn: () => api("/admin/plans") });
  const products = useQuery({ queryKey: ["products"], queryFn: () => api<{ id: string; slug: string }[]>("/admin/products") });
  const [productId, setProductId] = useState("");
  const [slug, setSlug] = useState("pro");
  const [name, setName] = useState("Pro");
  const [maxActivations, setMax] = useState(1);
  const mut = useMutation({
    mutationFn: () =>
      api("/admin/plans", {
        method: "POST",
        body: JSON.stringify({
          productId: productId || products.data?.[0]?.id,
          slug,
          name,
          maxActivations,
          features: ["builder", "premium_widgets", "advanced_colors", "library", "updates"],
        }),
      }),
  });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">پلن‌ها</h1>
      <form className="flex gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); mut.mutate(); }}>
        <select className="bg-panel border border-line rounded px-3" value={productId} onChange={(e) => setProductId(e.target.value)}>
          {(products.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>{p.slug}</option>
          ))}
        </select>
        <input className="bg-panel border border-line rounded px-3 py-2 ltr" value={slug} onChange={(e) => setSlug(e.target.value)} />
        <input className="bg-panel border border-line rounded px-3 py-2" value={name} onChange={(e) => setName(e.target.value)} />
        <input className="bg-panel border border-line rounded px-3 py-2 w-24" type="number" value={maxActivations} onChange={(e) => setMax(Number(e.target.value))} />
        <button className="bg-mint text-ink rounded px-4">ایجاد</button>
      </form>
      <JsonTable rows={q.data} />
    </div>
  );
}

function LicensesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["licenses"], queryFn: () => api("/admin/licenses") });
  const customers = useQuery({ queryKey: ["customers"], queryFn: () => api<{ id: string; email: string }[]>("/admin/customers") });
  const products = useQuery({ queryKey: ["products"], queryFn: () => api<{ id: string; plans: { id: string }[] }[]>("/admin/products") });
  const [rawKey, setRawKey] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => {
      const product = products.data?.[0];
      return api<{ license: { id: string }; raw_key: string }>("/admin/licenses", {
        method: "POST",
        body: JSON.stringify({
          customerId: customers.data?.[0]?.id,
          productId: product?.id,
          planId: product?.plans[0]?.id,
          status: "active",
        }),
      });
    },
    onSuccess: (data) => {
      setRawKey(data.raw_key);
      qc.invalidateQueries({ queryKey: ["licenses"] });
    },
  });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">لایسنس‌ها</h1>
      <button className="bg-mint text-ink rounded px-4 py-2 mb-4" onClick={() => mut.mutate()}>ایجاد لایسنس</button>
      {rawKey && (
        <div className="mb-4 rounded-lg border border-mint p-4 text-sm">
          کلید خام فقط یک‌بار نمایش داده می‌شود:
          <div className="ltr mt-2 font-mono">{rawKey}</div>
        </div>
      )}
      <JsonTable rows={q.data} />
    </div>
  );
}

function ReleasesPage() {
  const q = useQuery({ queryKey: ["releases"], queryFn: () => api("/admin/releases") });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">انتشارها</h1>
      <p className="text-sm text-zinc-400 mb-4">آپلود ZIP فقط برای نقش Owner از طریق POST /admin/releases/upload</p>
      <JsonTable rows={q.data} />
    </div>
  );
}

function AdminsPage() {
  const q = useQuery({ queryKey: ["admins"], queryFn: () => api("/admin/admins") });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">مدیران</h1>
      <JsonTable rows={q.data} />
    </div>
  );
}

function SecurityPage() {
  const q = useQuery({ queryKey: ["keys"], queryFn: () => api("/admin/security/signing-keys") });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">کلیدهای امضا</h1>
      <p className="text-sm text-zinc-500 mb-4">کلید خصوصی در دیتابیس ذخیره نمی‌شود. فقط public key و kid.</p>
      <JsonTable rows={q.data} />
    </div>
  );
}

function SettingsPage() {
  const q = useQuery({ queryKey: ["settings"], queryFn: () => api("/admin/settings") });
  return (
    <div>
      <h1 className="text-2xl font-semibold mb-4">تنظیمات</h1>
      <JsonTable rows={q.data} />
    </div>
  );
}
